/**
 * Kojiru on-chain subscription helper.
 *
 * Wraps the two-step USDC subscribe flow for the KojiruSubscription contract
 * (0x52fcA0998076b599c932F1647137DCBEb78FA839 on Base mainnet):
 *
 *   1. USDC.approve(subscriptionContract, price)   — one-time-per-price allowance
 *   2. KojiruSubscription.subscribe(tier, 0x0)     — routes USDC to treasury,
 *                                                    emits SubscriptionPaid event
 *
 * Backend event listener flips the buyer's tier in MongoDB when the
 * SubscriptionPaid event lands. Until that listener ships, the manual admin CLI
 * (backend/scripts/set_subscription_tier.py) handles fulfillment — the frontend
 * displays the tx hash so support can verify off-chain.
 *
 * Uses ethers v6 (already loaded by useKutroContracts.js). Attribution wrappers
 * from lib/erc8021 are intentionally NOT applied here: this is a USDC transfer,
 * not a KOJI protocol interaction, so ERC-8021 builder codes don't apply.
 */
import { ethers } from 'ethers';

const BASE_CHAIN_ID = 8453;

// USDC on Base — 6 decimals, canonical.
const USDC_ADDRESS = process.env.REACT_APP_USDC_BASE_ADDRESS
  || '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';

const SUBSCRIPTION_ADDRESS = process.env.REACT_APP_KOJIRU_SUBSCRIPTION_ADDRESS
  || '0x52fcA0998076b599c932F1647137DCBEb78FA839';

// Minimal ABIs — only the calls this flow needs.
const USDC_ABI = [
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function balanceOf(address who) view returns (uint256)',
  'function decimals() view returns (uint8)',
];

const SUBSCRIPTION_ABI = [
  'function subscribe(uint8 tier, bytes32 metadataHash)',
  'function priceFor(uint8 tier) view returns (uint256)',
  'function paused() view returns (bool)',
  'function treasury() view returns (address)',
  'event SubscriptionPaid(address indexed buyer, uint8 indexed tier, uint256 amountUSDC, uint32 periodSeconds, uint256 paidAt, bytes32 metadataHash)',
];

// Tier codes match KojiruSubscription.sol constants.
export const TIER = { PRO: 1, ENTERPRISE: 2 };

function tierCodeFor(planKey) {
  if (planKey === 'pro') return TIER.PRO;
  if (planKey === 'enterprise') return TIER.ENTERPRISE;
  throw new Error(`Unknown plan: ${planKey}`);
}

async function ensureBaseMainnet(provider) {
  const network = await provider.getNetwork();
  const chainId = Number(network.chainId);
  if (chainId !== BASE_CHAIN_ID) {
    // Try to switch — user may reject.
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x2105' }], // 8453
      });
    } catch (err) {
      throw new Error(`Please switch your wallet to Base mainnet (current: chain ${chainId}).`);
    }
  }
}

/**
 * Run the full subscribe flow. Returns { txHash, tier, priceUSDC, priceHuman }.
 *
 * @param {string} planKey  'pro' | 'enterprise'
 * @param {(step: string) => void} [onStep]  optional progress callback
 */
export async function subscribeOnChain(planKey, onStep = () => {}) {
  if (!window.ethereum) {
    throw new Error('No wallet detected. Install MetaMask, Rabby, or Coinbase Wallet.');
  }
  const tier = tierCodeFor(planKey);

  onStep('connecting');
  const provider = new ethers.BrowserProvider(window.ethereum);
  await provider.send('eth_requestAccounts', []);
  await ensureBaseMainnet(provider);

  const signer = await provider.getSigner();
  const buyer = await signer.getAddress();

  const subscription = new ethers.Contract(SUBSCRIPTION_ADDRESS, SUBSCRIPTION_ABI, signer);
  const usdc = new ethers.Contract(USDC_ADDRESS, USDC_ABI, signer);

  onStep('reading-price');
  const [priceUSDC, isPaused] = await Promise.all([
    subscription.priceFor(tier),
    subscription.paused(),
  ]);
  if (isPaused) throw new Error('Subscriptions are temporarily paused. Try again shortly.');

  const balance = await usdc.balanceOf(buyer);
  if (balance < priceUSDC) {
    const needHuman = ethers.formatUnits(priceUSDC, 6);
    const haveHuman = ethers.formatUnits(balance, 6);
    throw new Error(`Insufficient USDC on Base. Need ${needHuman}, have ${haveHuman}. Bridge USDC to Base first.`);
  }

  const allowance = await usdc.allowance(buyer, SUBSCRIPTION_ADDRESS);
  if (allowance < priceUSDC) {
    onStep('approving-usdc');
    const approveTx = await usdc.approve(SUBSCRIPTION_ADDRESS, priceUSDC);
    await approveTx.wait();
  }

  onStep('subscribing');
  // metadataHash left as bytes32(0) — off-chain listener uses buyer + tier + tx hash for lookup.
  const tx = await subscription.subscribe(tier, ethers.ZeroHash);
  const receipt = await tx.wait();

  onStep('confirmed');
  return {
    txHash: receipt.hash,
    tier,
    tierName: planKey,
    priceUSDC: priceUSDC.toString(),
    priceHuman: ethers.formatUnits(priceUSDC, 6),
    buyer,
    subscriptionAddress: SUBSCRIPTION_ADDRESS,
    explorer: `https://basescan.org/tx/${receipt.hash}`,
  };
}
